import { useCallback, useEffect, useState } from 'react';

import { persistentStorage } from '@/src/lib/persistent-storage';

import {
  DEFAULT_COMMENT_SORT_MODE,
  type CommentSortMode,
} from './comment-threading';

const COMMENT_SORT_STORAGE_KEY = 'frienzzi.commentSortMode.v1';

const sortModeListeners = new Set<(sortMode: CommentSortMode) => void>();

let currentSortMode = DEFAULT_COMMENT_SORT_MODE;
let loadSortModePromise: Promise<void> | null = null;

function isCommentSortMode(value: string | null): value is CommentSortMode {
  return value === 'recent' || value === 'oldest' || value === 'relevant';
}

function publishSortMode(sortMode: CommentSortMode) {
  currentSortMode = sortMode;
  sortModeListeners.forEach((listener) => listener(sortMode));
}

function loadSortMode() {
  if (loadSortModePromise) {
    return loadSortModePromise;
  }

  loadSortModePromise = persistentStorage
    .getItem(COMMENT_SORT_STORAGE_KEY)
    .then((storedSortMode) => {
      if (isCommentSortMode(storedSortMode)) {
        publishSortMode(storedSortMode);
      }
    })
    .catch(() => {
      // Keep the in-memory default if local storage is unavailable.
    });

  return loadSortModePromise;
}

export function useCommentSortPreference() {
  const [sortMode, setSortModeState] =
    useState<CommentSortMode>(currentSortMode);

  useEffect(() => {
    sortModeListeners.add(setSortModeState);
    void loadSortMode();

    return () => {
      sortModeListeners.delete(setSortModeState);
    };
  }, []);

  const setSortMode = useCallback((nextSortMode: CommentSortMode) => {
    if (nextSortMode === currentSortMode) {
      return;
    }

    publishSortMode(nextSortMode);
    void persistentStorage.setItem(COMMENT_SORT_STORAGE_KEY, nextSortMode);
  }, []);

  return { setSortMode, sortMode };
}
